import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getQueryFn } from "@/lib/queryClient";
import { PageHeader } from "@/components/shared/page-header";
import { DataTable, type ColumnDef } from "@/components/shared/data-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { BrainCircuit } from "lucide-react";
import { formatDateTime } from "@/lib/format";
import type { AiScopingLog } from "@db/schema";

interface AiScopingLogsResponse {
  data: AiScopingLog[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export default function AiScopingLogsPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedLog, setSelectedLog] = useState<AiScopingLog | null>(null);

  const queryParams = new URLSearchParams();
  queryParams.set("page", String(page));
  queryParams.set("limit", "20");
  if (search.trim()) queryParams.set("search", search.trim());
  if (startDate) queryParams.set("startDate", startDate);
  if (endDate) queryParams.set("endDate", endDate);

  const { data, isLoading } = useQuery<AiScopingLogsResponse>({
    queryKey: [`/api/intake/ai-scoping-logs?${queryParams.toString()}`],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const columns: ColumnDef<AiScopingLog>[] = [
    {
      key: "category",
      header: "Category",
      cell: (row) => <span className="text-sm">{row.category}</span>,
      className: "w-[150px]",
    },
    {
      key: "contactEmail",
      header: "Contact Email",
      cell: (row) => (
        <span className="text-sm">{row.contactEmail || "\u2014"}</span>
      ),
    },
    {
      key: "durationMs",
      header: "Duration (ms)",
      cell: (row) => (
        <span className="text-sm text-muted-foreground font-mono">
          {row.durationMs != null ? `${row.durationMs}ms` : "\u2014"}
        </span>
      ),
      className: "w-[120px]",
    },
    {
      key: "error",
      header: "Error?",
      cell: (row) =>
        row.error ? (
          <Badge className="bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20">
            Error
          </Badge>
        ) : (
          <Badge className="bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20">
            OK
          </Badge>
        ),
      className: "w-[80px]",
    },
    {
      key: "createdAt",
      header: "Created At",
      cell: (row) => (
        <span className="text-sm text-muted-foreground">
          {formatDateTime(row.createdAt)}
        </span>
      ),
      className: "w-[160px]",
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="AI Scoping Logs"
        description="View logs of AI scoping requests and responses"
      />

      <DataTable
        columns={columns}
        data={data?.data}
        isLoading={isLoading}
        rowKey={(row) => row.id}
        onRowClick={(row) => setSelectedLog(row)}
        searchValue={search}
        onSearchChange={(val) => {
          setSearch(val);
          setPage(1);
        }}
        searchPlaceholder="Search by email or category..."
        emptyIcon={BrainCircuit}
        emptyTitle="No AI scoping logs"
        emptyDescription="AI scoping logs will appear here when work requests are processed."
        pagination={
          data?.pagination
            ? {
                page: data.pagination.page,
                totalPages: data.pagination.totalPages,
                onPageChange: setPage,
              }
            : undefined
        }
        filterSlot={
          <div className="flex items-end gap-2">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">
                Start Date
              </Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setPage(1);
                }}
                className="w-[160px]"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">End Date</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setPage(1);
                }}
                className="w-[160px]"
              />
            </div>
            {(startDate || endDate) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setStartDate("");
                  setEndDate("");
                  setPage(1);
                }}
              >
                Clear
              </Button>
            )}
          </div>
        }
      />

      {/* Log Detail Dialog */}
      <Dialog
        open={!!selectedLog}
        onOpenChange={(open) => {
          if (!open) setSelectedLog(null);
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>AI Scoping Log Detail</DialogTitle>
            <DialogDescription>
              {selectedLog?.category} &mdash;{" "}
              {formatDateTime(selectedLog?.createdAt)}
              {selectedLog?.contactEmail &&
                ` &mdash; ${selectedLog.contactEmail}`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Metadata row */}
            <div className="flex gap-4 text-sm">
              {selectedLog?.durationMs != null && (
                <div>
                  <span className="text-muted-foreground">Duration: </span>
                  <span className="font-mono">{selectedLog.durationMs}ms</span>
                </div>
              )}
              {selectedLog?.workRequestId && (
                <div>
                  <span className="text-muted-foreground">
                    Work Request ID:{" "}
                  </span>
                  <span className="font-mono text-xs">
                    {selectedLog.workRequestId}
                  </span>
                </div>
              )}
            </div>

            {/* Error */}
            {selectedLog?.error && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                <h4 className="text-sm font-semibold text-red-700 dark:text-red-400 mb-1">
                  Error
                </h4>
                <pre className="text-sm text-red-700 dark:text-red-400 whitespace-pre-wrap">
                  {selectedLog.error}
                </pre>
              </div>
            )}

            {/* Prompt */}
            <div>
              <h4 className="text-sm font-semibold mb-2">Prompt</h4>
              <ScrollArea className="h-[200px] rounded-md border p-3">
                <pre className="text-sm whitespace-pre-wrap font-mono">
                  {selectedLog?.prompt ?? "No prompt recorded"}
                </pre>
              </ScrollArea>
            </div>

            {/* Response */}
            <div>
              <h4 className="text-sm font-semibold mb-2">Response</h4>
              <ScrollArea className="h-[200px] rounded-md border p-3">
                <pre className="text-sm whitespace-pre-wrap font-mono">
                  {selectedLog?.response ?? "No response recorded"}
                </pre>
              </ScrollArea>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
